/**
 * « LE MUR DIT CE QU'IL A » — l'instrument de diagnostic de la Veillée.
 *
 * POURQUOI IL EXISTE, ET POURQUOI IL NE RESSEMBLE À RIEN D'AUTRE ICI
 * ------------------------------------------------------------------
 * Le 12/08, `?display=conte` a rendu un écran NOIR sur l'iPhone du
 * propriétaire — mesuré image par image : aucun pixel au-dessus de 10/255
 * sur toute la zone de contenu. Or le fond par défaut du document est
 * IVOIRE (index.css le pose sur `body`) : un bundle qui n'aurait pas démarré
 * aurait donné de l'ivoire. Le noir est donc PEINT par du code à nous.
 * Quelque chose se monte, peint son fond noir, et ne rend rien dedans. Le
 * raisonnement statique s'arrête là ; il faut un instrument.
 *
 * LA CONTRAINTE QUI DICTE TOUT LE RESTE : un diagnostic qui exige que
 * l'application fonctionne pour s'afficher ne sert à rien. Celui-ci est
 * donc, délibérément, la chose la moins « app » du dépôt :
 *
 *   · il se peint en DOM NU, sans React — il ne dépend ni du rendu, ni du
 *     routeur, ni de la résolution d'un chunk paresseux ;
 *   · il est monté depuis `src/index.js`, AVANT `root.render()` — s'il
 *     existe un écran, il existe déjà quand l'écran essaie de naître ;
 *   · il vit HORS de `#root` — un démontage de React ne l'emporte pas ;
 *   · il n'importe que des CONSTANTES (le build id, les testids, le
 *     prédicat `?debug=1`) : aucune dépendance qui puisse elle-même
 *     échouer à charger.
 *
 * LES COULEURS. La charte interdit les hex en dur, et ce module n'a pas de
 * classes Tailwind « JIT-générées » à la volée : il porte donc des noms de
 * CLASSES littéraux (`bg-manoir-delft-deep`, `text-aman-ivory`…), que le
 * scanner Tailwind voit comme n'importe quel JSX puisqu'il balaie
 * `./src/**\/*.js`. Et le repli est bon : si la feuille de style n'était pas
 * chargée, le mur ne serait pas noir non plus (le corps du document est
 * ivoire) et le panneau se lirait en noir sur ivoire. Il est lisible dans
 * les deux mondes — c'est exactement ce qu'on lui demande.
 *
 * LA GRAMMAIRE. Celle de l'AR, sans une virgule de différence : un seul
 * paramètre `?debug=1` (lib/debugMode.js), rien pour l'invité, aucune
 * traduction (outil d'opérateur, français de la maison), et surtout AUCUN
 * effet quand il est absent — pas d'écouteur, pas de minuterie, pas de
 * nœud. `reportWall()` est alors un retour immédiat, ce qui autorise
 * l'écran à l'appeler depuis son rendu sans y penser.
 *
 * CE QU'IL DIT, et l'ordre est celui d'une enquête :
 *   1. quel bundle tourne, et si un service worker contrôle la page
 *      (deux façons classiques de regarder un écran qui n'existe plus) ;
 *   2. la fenêtre — taille et rapport de forme (le mur fait 16/9, le
 *      téléphone du propriétaire 0,46 : ce n'est pas le même écran) ;
 *   3. l'état du chargement : en cours / réussi / échoué, le code HTTP,
 *      le temps écoulé ;
 *   4. ce que les données contiennent — livres, livre choisi, planches ;
 *   5. QUELLE BRANCHE REND. C'est le renseignement central : il nomme
 *      lequel des cinq cas est en train de peindre du vide ;
 *   6. la dernière erreur JavaScript, message compris.
 */
import { BUILD_ID } from '@/constants/manoir';
import { PROJECTION } from '@/constants/testIds';
import { isDebugMode } from '@/lib/debugMode';

/** Le mur, et lui seul : `?display=conte`. */
export function isWallRoute() {
    try {
        return new URLSearchParams(window.location.search).get('display') === 'conte';
    } catch {
        return false;
    }
}

const now = () => (typeof performance !== 'undefined' && performance.now
    ? performance.now() : Date.now());

/** Les six branches de rendu du mur, nommées en clair. La clé est celle que
 *  l'écran calcule pour CHOISIR quoi rendre — pas un doublon qui pourrait
 *  diverger : c'est la même valeur qui décide et qui est rapportée. */
export const BRANCH_LABELS = {
    attente: 'ATTENTE — le vestibule (les données arrivent)',
    traine: 'TRAÎNE — le vestibule qui avoue patienter',
    echec: 'ÉCHEC — la carte honnête (réessayer)',
    etagere: 'ÉTAGÈRE — la Bibliothèque',
    introuvable: 'INTROUVABLE — le livre demandé ne se lit pas',
    livre: 'LIVRE — le livre ouvert',
};

const FILM_LABELS = {
    descente: 'la Descente (grue scrubbée)',
    intro: "le film d'intro",
    aucun: 'aucun',
};

const EMPTY = '—';

/** L'état du monde, tel que le panneau le connaît. Un seul objet, muté en
 *  place : le panneau n'est pas un composant, il n'a pas d'état à réconcilier. */
const facts = {
    boot: now(),
    served: null,        // l'id servi par /build-id.json (null = pas encore lu)
    mounted: false,      // ProjectionConteScreen a-t-il rendu UNE fois ?
    branch: null,
    film: null,
    data: null,          // { state, status, elapsed, message }
    content: null,       // { books, chosen, planches, pages }
    layout: null,        // { single, spread, inBook, face, leaf, leaves }
    pointer: null,       // { finger, coarse, geste }
    error: null,         // { where, message }
};

let armed = false;       // `?debug=1` ET le mur — décidé une fois, au montage
let panel = null;
let rows = null;
let ticker = null;

/**
 * Verse un fait au dossier. NO-OP hors mode diagnostic — c'est la condition
 * pour que l'écran puisse appeler ceci sans se demander ce que ça coûte.
 */
export function reportWall(patch) {
    if (!armed || !patch) return;
    Object.assign(facts, patch);
    paint();
}

/**
 * La dernière erreur JavaScript. Enregistrée même hors mode diagnostic ?
 * NON — la règle est « aucun effet quand il est absent », et une frontière
 * d'erreur qui plante affiche de toute façon son message elle-même. Ici on
 * ne garde que ce que le panneau saura montrer.
 */
export function noteWallError(where, err) {
    if (!armed) return;
    let message = '';
    try {
        message = (err && (err.message || err.reason?.message || String(err.reason) || String(err))) || '';
    } catch { message = 'erreur illisible'; }
    facts.error = { where, message: String(message).slice(0, 400) };
    paint();
}

/** La fenêtre, telle que le navigateur la donne à cet instant. */
function viewportLine() {
    if (typeof window === 'undefined') return EMPTY;
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    const dpr = window.devicePixelRatio || 1;
    const ratio = h ? (w / h) : 0;
    const shape = ratio >= 1 ? 'paysage' : 'portrait';
    return `${w}×${h} · dpr ${dpr} · ${ratio.toFixed(2)} ${shape}`;
}

function bundleLine() {
    const baked = BUILD_ID || EMPTY;
    if (facts.served === null) return `${baked} · servi : ?`;
    if (facts.served === '') return `${baked} · servi : illisible`;
    if (facts.served === baked) return `${baked} · servi : le même`;
    return `${baked} · SERVI ${facts.served} — PÉRIMÉ`;
}

function swLine() {
    try {
        if (!('serviceWorker' in navigator)) return 'non pris en charge';
        const c = navigator.serviceWorker.controller;
        if (!c) return 'aucun ne contrôle la page';
        const url = String(c.scriptURL || '');
        return `OUI · ${url.split('/').pop() || url} contrôle la page`;
    } catch {
        return 'illisible';
    }
}

const seconds = (ms) => `${(ms / 1000).toFixed(1)} s`;

function dataLine() {
    const d = facts.data;
    if (!d) return `inconnu — l'écran n'a rien rapporté (${seconds(now() - facts.boot)} depuis le démarrage)`;
    const bits = [];
    if (d.state === 'ok') bits.push('RÉUSSI');
    else if (d.state === 'echec') bits.push('ÉCHOUÉ');
    else bits.push('EN COURS');
    if (d.state === 'echec') bits.push(d.status ? `HTTP ${d.status}` : 'aucune réponse (réseau / délai)');
    bits.push(seconds(d.elapsed != null ? d.elapsed : now() - facts.boot));
    if (d.message) bits.push(d.message);
    return bits.join(' · ');
}

function contentLine() {
    const c = facts.content;
    if (!c) return EMPTY;
    return [
        `${c.books} livres`,
        `choisi : ${c.chosen || 'aucun'}`,
        `${c.planches} planches`,
        `${c.pages} pages`,
    ].join(' · ');
}

/** « LE VRAI » — LA LIGNE QUI ÉCOUTE `orphans`.
 *
 *  Le découpage des crédits (Loi 2 : la SAVA, l'INRAP, Bibracte) rend depuis
 *  toujours un signal d'échec bruyant, `orphans` — de la matière qui ne tient
 *  sur AUCUNE face et que le rendu imprimera donc sous `overflow-hidden`. Un
 *  grep du dépôt ne le trouvait que dans un test unitaire : personne ne
 *  l'écoutait à l'exécution. Pendant ce temps, sous `?pages=double` en
 *  portrait, 149 px de crédits disparaissaient en silence.
 *
 *  Il est donc ici, dans le seul instrument qu'on ouvre quand on doute d'un
 *  écran — avec ce qui permet de comprendre pourquoi : la boîte de papier
 *  RÉELLEMENT mesurée (contre celle que le modèle prédisait), le corps retenu,
 *  et le remplissage de chaque face. « Des pages presque vides » et « des
 *  crédits coupés » sont deux maladies du même organe, et cette ligne les
 *  montre toutes les deux d'un coup d'œil. */
function vraiLine() {
    const v = facts.vrai;
    if (!v) return EMPTY;
    if (!v.faces) return 'aucun crédit sur ce livre';
    const box = v.box ? `${Math.round(v.box.w)}×${Math.round(v.box.h)}` : '?';
    const fills = (v.fills || []).map((f) => `${Math.round(f * 100)}%`).join(' · ');
    const alarm = v.orphans ? ` — ${v.orphans} ORPHELINE(S), DE LA MATIÈRE EST CACHÉE` : ' — 0 orpheline';
    // Le pied laissé au voile de la plaque de fin : imprimé dès qu'il mord,
    // parce qu'une face « seulement » aux trois quarts pleine s'explique là et
    // nulle part ailleurs.
    const foot = v.foot > 0.5 ? ` · pied de plaque ${Math.round(v.foot)} px` : '';
    return `${v.faces} face(s) · boîte ${v.measured ? 'MESURÉE' : 'prédite'} ${box}${foot}`
        + ` · corps ${Math.round(v.body)} px · remplissage ${fills}${alarm}`;
}

/** QUEL LIVRE REND — la double page du mur, ou la page unique de la main.
 *
 *  Cette ligne manquait le 12/08, et son absence a coûté trois jours : le
 *  panneau disait « LIVRE — le livre ouvert », ce qui était VRAI, et l'écran
 *  était noir aux trois quarts. « La branche rend » et « ce qu'elle rend tient
 *  dans la fenêtre » sont deux questions différentes ; celle-ci répond à la
 *  seconde. Comme la branche, c'est la valeur qui DÉCIDE de la forme du livre
 *  qui est rapportée — pas un second avis sur elle. */
function layoutLine() {
    const l = facts.layout;
    if (!l) return EMPTY;
    const forced = l.spread && l.spread !== 'auto' ? ` · FORCÉ (?pages=${l.spread})` : '';
    if (!l.single) return `DOUBLE PAGE — la prose et la planche côte à côte${forced}`;
    const face = l.face === 'plate' ? 'la planche' : 'le texte';
    const where = l.inBook && l.leaves ? ` · feuillet ${(l.leaf || 0) + 1}/${l.leaves}` : '';
    return `PAGE UNIQUE — ${face}${where}${forced}`;
}

/** QUI TIENT L'ÉCRAN — un doigt, ou une télécommande.
 *
 *  Le 12/08 au soir, le propriétaire a cru une heure durant que l'app était
 *  cassée sur son iPhone : elle lui réclamait une touche « OK ». Le panneau
 *  savait déjà dire quelle branche rendait et quel livre s'affichait ; il ne
 *  savait pas dire à QUI l'écran croyait parler. C'est la question qu'il
 *  fallait poser, donc c'est une ligne. On rapporte la valeur qui DÉCIDE
 *  (`finger`), et à côté d'elle la mesure brute (`coarse`) : quand les deux
 *  divergent, c'est le témoin `?geste=` qui parle, et il le dit. */
function pointerLine() {
    const p = facts.pointer;
    if (!p) return EMPTY;
    const forced = p.geste && p.geste !== 'auto' ? ` · FORCÉ (?geste=${p.geste})` : '';
    const measured = p.coarse ? 'hover:none + pointer:coarse' : 'un pointeur fin qui survole';
    return `${p.finger ? 'LE DOIGT' : 'LA TÉLÉCOMMANDE'} — mesuré : ${measured}${forced}`;
}

function branchLine() {
    if (!facts.mounted) return "AUCUNE — l'écran du mur n'a JAMAIS rendu";
    if (!facts.branch) return 'AUCUNE — rendu monté mais branche non rapportée';
    return BRANCH_LABELS[facts.branch] || facts.branch;
}

function errorLine() {
    if (!facts.error) return 'aucune';
    return `[${facts.error.where}] ${facts.error.message || 'sans message'}`;
}

/** L'ordre est celui de l'enquête ; la BRANCHE est la ligne qu'on lit en
 *  premier même quand on lit mal, donc elle est la plus grosse. */
const ROWS = [
    ['branche', 'BRANCHE RENDUE', branchLine, true, PROJECTION.diagBranch],
    ['livre', 'Livre rendu', layoutLine, false, PROJECTION.diagLayout],
    ['geste', 'Instrument', pointerLine, false, PROJECTION.diagPointer],
    ['vrai', 'Le Vrai (crédits, Loi 2)', vraiLine, false, PROJECTION.diagVrai],
    ['film', 'Calque au-dessus', () => FILM_LABELS[facts.film] || (facts.mounted ? 'aucun' : EMPTY), false],
    ['donnees', 'Données', dataLine, false],
    ['contenu', 'Contenu', contentLine, false],
    ['bundle', 'Bundle', bundleLine, false],
    ['sw', 'Service worker', swLine, false],
    ['fenetre', 'Fenêtre', viewportLine, false],
    ['erreur', 'Dernière erreur JS', errorLine, false],
];

function paint() {
    if (!panel || !rows) return;
    ROWS.forEach(([key, , read]) => {
        const node = rows[key];
        if (!node) return;
        let text;
        try { text = read(); } catch (e) { text = `sonde en défaut : ${e && e.message}`; }
        const next = String(text);
        if (node.textContent !== next) node.textContent = next;
    });
}

function makeRow(doc, label, big, testId) {
    const wrap = doc.createElement('div');
    wrap.style.marginTop = big ? '0' : '0.5rem';

    const tag = doc.createElement('div');
    tag.className = 'text-manoir-gold-soft';
    tag.style.fontSize = '0.62rem';
    tag.style.letterSpacing = '0.16em';
    tag.style.textTransform = 'uppercase';
    tag.textContent = label;

    const value = doc.createElement('div');
    if (testId) value.setAttribute('data-testid', testId);
    value.className = 'text-aman-ivory';
    value.style.fontSize = big ? '1.25rem' : '0.9rem';
    value.style.fontWeight = big ? '700' : '400';
    value.style.lineHeight = '1.3';
    value.style.marginTop = '0.1rem';
    value.style.wordBreak = 'break-word';

    wrap.appendChild(tag);
    wrap.appendChild(value);
    return { wrap, value };
}

/**
 * Monte le panneau. Idempotent, silencieux hors `?display=conte&debug=1`.
 * Appelé depuis `src/index.js` avant le rendu de React — c'est ce qui rend
 * le diagnostic ATTEIGNABLE quand tout le reste est noir.
 */
export function mountWallDiagnostics() {
    if (panel) return panel;
    if (typeof document === 'undefined' || !document.body) return null;
    if (!isWallRoute() || !isDebugMode()) return null;

    armed = true;
    facts.boot = now();

    const doc = document;
    panel = doc.createElement('div');
    panel.setAttribute('data-testid', PROJECTION.diag);
    panel.setAttribute('role', 'status');
    // Les COULEURS viennent des jetons de la charte (classes littérales, vues
    // par le scanner Tailwind) ; la GÉOMÉTRIE est en ligne, parce qu'elle ne
    // doit rien à personne : c'est ce qui survit à une feuille manquante.
    panel.className = 'bg-manoir-delft-deep text-aman-ivory border-manoir-gold';
    Object.assign(panel.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '2147483000',
        boxSizing: 'border-box',
        padding: 'calc(env(safe-area-inset-top, 0px) + 0.75rem) 0.9rem 0.9rem',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        // Un instrument ne se met jamais entre l'opérateur et l'écran qu'il
        // observe : il se lit, il ne s'attrape pas. Aucun élément
        // interactif, donc aucune cible tactile à dimensionner.
        pointerEvents: 'none',
        maxHeight: '100%',
        overflow: 'hidden',
    });

    const head = doc.createElement('div');
    head.className = 'text-manoir-gold';
    head.style.fontSize = '0.68rem';
    head.style.letterSpacing = '0.2em';
    head.style.textTransform = 'uppercase';
    head.style.marginBottom = '0.7rem';
    head.textContent = 'Diagnostic · le mur · debug=1';
    panel.appendChild(head);

    rows = {};
    ROWS.forEach(([key, label, , big, testId]) => {
        const { wrap, value } = makeRow(doc, label, big, testId);
        rows[key] = value;
        panel.appendChild(wrap);
    });

    doc.body.appendChild(panel);
    paint();

    // Les trois sources qui parlent d'elles-mêmes.
    try {
        window.addEventListener('error', (e) => noteWallError('window.error', e.error || e));
        window.addEventListener('unhandledrejection', (e) => noteWallError('promesse', e));
        window.addEventListener('resize', paint);
    } catch { /* un navigateur sans écouteurs n'existe pas, mais le panneau ne meurt pas pour ça */ }

    // Le temps qui passe est une donnée : une attente de douze secondes se
    // lit sur le panneau, elle ne se devine pas.
    try { ticker = setInterval(paint, 500); } catch { ticker = null; }

    // Le bundle SERVI — la question « suis-je en train de regarder une vieille
    // version ? », à laquelle personne ne peut répondre depuis un téléphone.
    try {
        fetch('/build-id.json', { cache: 'no-store' })
            .then((r) => (r.ok ? r.json() : null))
            .then((d) => { facts.served = (d && d.buildId ? String(d.buildId) : '').trim(); paint(); })
            .catch(() => { facts.served = ''; paint(); });
    } catch { facts.served = ''; }

    return panel;
}

/** Démontage — pour les tests, et pour eux seuls (le mur ne se démonte pas). */
export function unmountWallDiagnostics() {
    if (ticker) { clearInterval(ticker); ticker = null; }
    if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
    panel = null;
    rows = null;
    armed = false;
    facts.served = null;
    facts.mounted = false;
    facts.branch = null;
    facts.film = null;
    facts.data = null;
    facts.content = null;
    facts.layout = null;
    facts.pointer = null;
    facts.error = null;
}
